import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../UI/LoadingSpinner';
import { PlayerDataContext, SocketContext } from '../../App';
import useSocketData from '../../hooks/useSocketData';
import Map from './Map/Map';
import Navbar from '../Navbar/Navbar';
import Overlay from '../Overlay/Overlay';
import styles from './Gameboard.module.css';
import trophyImage from '../../images/trophy.webp';
import toastManager from '../../utils/toastManager';
import soundManager from '../../utils/soundManager';
import pushNotifications from '../../utils/pushNotifications';
import { celebrateBurst } from '../UI/confetti';
import BoardModeTabs from '../UI/BoardModeTabs';
import Dice from '../Navbar/Dice/Dice';
import diceImages from '../../constants/diceImages';
import rollingGif from '../../images/dice/rolling.gif';
import ReadyButton from '../Navbar/ReadyButton/ReadyButton';
import VoiceChat from '../RTC/VoiceChat';
import { getVisiblePawns } from '../../utils/gameUtils';
import { describeOpenPawnRule, normalizeOpenPawnValues } from '../../utils/openPawnRules';
import { normalizeRoomMode, readStoredRoomMode } from '../../utils/roomMode';
import { TWO_PLAYER_VISIBLE_COLORS, getTwoPlayerVisibleHex } from '../../utils/twoPlayerColors';

// ── Emoji reactions ────────────────────────────────────────────────────────
const EMOJIS = ['😂', '😍', '😎', '😡', '🤩', '😭', '👏', '🔥', '💪', '🎲', '🏆', '👋'];
const AVATAR_PALETTE = ['#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#db2777', '#0891b2', '#ca8a04', '#dc2626'];

const buildAvatarDataUri = (name = 'Player', accent = '#2563eb', forceAccent = false) => {
    const first = (name.trim()[0] || 'P').toUpperCase();
    const seed = Array.from(name).reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
    const bg = forceAccent ? accent : AVATAR_PALETTE[seed % AVATAR_PALETTE.length];
    const pattern = seed % 4;
    const shapes = [
        `<circle cx="54" cy="48" r="28" fill="rgba(255,255,255,0.16)" />`,
        `<rect x="26" y="22" width="56" height="56" rx="16" fill="rgba(255,255,255,0.14)" />`,
        `<path d="M18 68C32 34 76 22 92 54C102 74 84 98 56 96C34 94 14 84 18 68Z" fill="rgba(255,255,255,0.14)" />`,
        `<path d="M54 16L66 38L90 42L72 60L76 86L54 74L32 86L36 60L18 42L42 38Z" fill="rgba(255,255,255,0.12)" />`,
    ];
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="108" height="108" viewBox="0 0 108 108">
            <defs>
                <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stop-color="${bg}" />
                    <stop offset="100%" stop-color="${accent}" />
                </linearGradient>
            </defs>
            <rect width="108" height="108" rx="28" fill="url(#g)" />
            ${shapes[pattern]}
            <circle cx="54" cy="40" r="18" fill="rgba(255,255,255,0.94)" />
            <path d="M24 94c4-20 18-30 30-30s26 10 30 30" fill="rgba(255,255,255,0.92)" />
            <text x="54" y="47" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="800" fill="${bg}">${first}</text>
        </svg>
    `;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

// ── Hearts / Lives display ─────────────────────────────────────────────────
const MAX_LIVES = 3;
const PREF_KEYS = {
    showChat: 'ludoPrefShowChat',
    showVoiceChat: 'ludoPrefShowVoiceChat',
    micMuted: 'ludoPrefMicMuted',
};

const normalizeWinnerEvent = value => {
    if (!value) return null;
    if (typeof value === 'string') {
        return { color: value, name: null, isCurrentPlayer: false };
    }
    if (typeof value === 'object') {
        return {
            color: value.color || value.winnerColor || value.winner || null,
            name: value.name || value.winnerName || value.playerName || null,
            isCurrentPlayer: value.isCurrentPlayer === true,
        };
    }
    return null;
};

const describeTimerDecision = reason => ({
    total_progress: 'Highest total pawn progress',
    highest_single_pawn: 'Equal total • highest single pawn won',
    fewer_progressed_pawns: 'Equal score • fewer progressed pawns won',
    home_pawns: 'Equal score • more Home pawns won',
    seat_order: 'Exact tie • seat order decided',
}[reason] || 'Timer score');

const HeartsDisplay = ({ lives, broken, compact = false }) => {
    const count = lives != null ? lives : MAX_LIVES;
    return (
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {Array.from({ length: MAX_LIVES }).map((_, i) => {
                const filled = i < count;
                const lostIndex = Math.min(count, MAX_LIVES - 1);
                const isBreaking = broken && i === lostIndex;
                return (
                    <span key={i} style={{
                        fontSize: compact ? 11 : 13,
                        lineHeight: 1,
                        filter: filled ? 'none' : 'grayscale(1) opacity(0.35)',
                        animation: isBreaking ? 'heartBreak 0.6s ease-out' : 'none',
                        display: 'inline-block',
                        transform: isBreaking ? 'scale(1.4)' : 'scale(1)',
                        transition: 'transform 0.3s',
                    }}>
                        {isBreaking ? '💔' : filled ? '❤️' : '🖤'}
                    </span>
                );
            })}
        </div>
    );
};

const TopIconButton = ({ title, onClick, children, active = false, danger = false, disabled = false }) => (
    <button
        title={title}
        onClick={onClick}
        disabled={disabled}
        style={{
            position: 'relative',
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: '1px solid transparent',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: danger ? '#ff6b6b' : active ? '#00d4ff' : 'rgba(255,255,255,0.9)',
            padding: 0,
            flexShrink: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.45 : 1,
            boxShadow: 'none',
        }}
    >
        {children}
    </button>
);

const MicGlyph = ({ muted = false, active = false }) => (
    <span style={{ position: 'relative', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={muted ? '#ff6b6b' : active ? '#00e676' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <path d="M12 19v3" />
            <path d="M8 22h8" />
        </svg>
        {muted && (
            <span style={{
                position: 'absolute',
                width: 22,
                height: 2.5,
                background: '#ff6b6b',
                borderRadius: 999,
                transform: 'rotate(-45deg)',
                boxShadow: '0 0 8px rgba(255,107,107,0.5)',
            }} />
        )}
    </span>
);

const ExitGlyph = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="M16 17l5-5-5-5" />
        <path d="M21 12H9" />
    </svg>
);

const CopyGlyph = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
);

// ── Self-ticking timers ─────────────────────────────────────────────────────
// These own their 1-second tick so the (large) Gameboard tree doesn't re-render
// every second just to repaint a countdown.
const TurnTimer = React.memo(({ time }) => {
    const [left, setLeft] = useState(0);
    useEffect(() => {
        if (!time) {
            setLeft(0);
            return undefined;
        }
        const update = () => setLeft(Math.max(0, Math.ceil((time - Date.now()) / 1000)));
        update();
        const timer = setInterval(update, 1000);
        return () => clearInterval(timer);
    }, [time]);
    return (
        <div
            style={{
                minWidth: 56,
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: left <= 5 ? '#ff7b72' : left <= 10 ? '#facc15' : 'rgba(255,255,255,0.88)',
            }}
        >
            {String(Math.floor(left / 60)).padStart(2, '0')}:{String(left % 60).padStart(2, '0')}
        </div>
    );
});

const MatchTimer = React.memo(({ endTime, minutes }) => {
    const [left, setLeft] = useState(0);
    useEffect(() => {
        if (!endTime) {
            setLeft(0);
            return undefined;
        }
        const update = () => setLeft(Math.max(0, Math.ceil((Number(endTime) - Date.now()) / 1000)));
        update();
        const timer = setInterval(update, 250);
        return () => clearInterval(timer);
    }, [endTime]);
    return (
        <div
            title={`${minutes || 2} minute Timer Race`}
            style={{
                minWidth: 72,
                padding: '4px 7px',
                borderRadius: 9,
                textAlign: 'center',
                background: left <= 15 ? 'rgba(239,68,68,0.18)' : 'rgba(6,182,212,0.14)',
                border: `1px solid ${left <= 15 ? 'rgba(248,113,113,0.55)' : 'rgba(103,232,249,0.38)'}`,
                color: left <= 10 ? '#fca5a5' : '#a5f3fc',
                fontSize: 12,
                lineHeight: 1.1,
                fontWeight: 900,
                letterSpacing: '0.06em',
            }}
        >
            <div style={{ fontSize: 8, opacity: 0.72, marginBottom: 2 }}>MATCH</div>
            {String(Math.floor(left / 60)).padStart(2, '0')}:{String(left % 60).padStart(2, '0')}
        </div>
    );
});

// Compact creator/start status shown before a 2-player match begins.
const WaitingDiceBox = React.memo(({
    compact = false,
    isCreator = false,
    canStart = false,
    joinedPlayers = 0,
    minimumPlayers = 2,
}) => {
    const statusText = !isCreator
        ? 'Waiting for room creator'
        : canStart
            ? `${joinedPlayers}/${minimumPlayers} ready to start`
            : `Waiting for friend ${joinedPlayers}/${minimumPlayers}`;
    return (
        <div className={styles.diceDock} style={{
            width: compact ? 82 : 96, height: compact ? 82 : 96,
            borderRadius: 16,
            background: canStart
                ? 'linear-gradient(145deg, rgba(34,197,94,0.24), rgba(22,163,74,0.18))'
                : 'rgba(255,255,255,0.06)',
            border: canStart ? '2px solid rgba(250,204,21,0.72)' : '2px solid rgba(255,255,255,0.15)',
            boxShadow: canStart
                ? '0 0 22px rgba(34,197,94,0.3), 0 4px 14px rgba(0,0,0,0.5)'
                : '0 4px 14px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'all 0.3s ease',
            margin: compact ? '0 3px' : '0 8px',
        }}>
            <div style={{ width: '100%', padding: compact ? '5px' : '7px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: compact ? 15 : 17, lineHeight: 1 }}>
                    {canStart ? '👑' : '🎲'}
                </div>
                <ReadyButton
                    dock
                    creatorStart
                    isCreator={isCreator}
                    canStart={canStart}
                />
                <div style={{
                    fontSize: 8.5, fontWeight: 800,
                    color: canStart ? '#86efac' : '#cbd5e1',
                    textAlign: 'center', lineHeight: 1.15,
                }}>
                    {statusText}
                </div>
            </div>
        </div>
    );
});

// ── Ludo King-style avatar box ──────────────────────────────────────────────
const PlayerAvatar = React.memo(({ player, isMoving, flip = false, onSendEmoji, compact = false }) => {
    const [showEmoji, setShowEmoji] = useState(false);
    const initial = (player?.name || '?')[0].toUpperCase();
    const color = player?.color || '#888888';
    const visibleKey = player?.visibleKey || color;
    const forceDisplayColor = Boolean(player?.logicalColor);
    const avatarSrc = useMemo(
        () => buildAvatarDataUri(player?.name || 'Player', color, forceDisplayColor),
        [player?.name, color, forceDisplayColor]
    );
    const toggleEmoji = () => setShowEmoji(v => !v);
    const sendEmoji = (e) => {
        if (onSendEmoji) onSendEmoji(e);
        setShowEmoji(false);
    };
    return (
        <div style={{ position: 'relative', display: 'flex', flexDirection: flip ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 6 }}>
            {/* Emoji picker popup */}
            {showEmoji && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    [flip ? 'right' : 'left']: 0,
                    marginBottom: 8,
                    background: 'rgba(10,15,40,0.96)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 14,
                    padding: '8px 10px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 4,
                    zIndex: 100,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(12px)',
                    minWidth: 180,
                }}>
                    {EMOJIS.map(e => (
                        <button key={e} onClick={() => sendEmoji(e)} style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            fontSize: 22, padding: 4, borderRadius: 8, lineHeight: 1,
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                        >{e}</button>
                    ))}
                </div>
            )}
            <div
                onClick={toggleEmoji}
                data-visible-color={visibleKey}
                style={{
                    width: compact ? 54 : 64, height: compact ? 64 : 76, borderRadius: compact ? 7 : 8,
                    border: `3px solid ${isMoving ? '#facc15' : showEmoji ? '#ffffff' : color}`,
                    background: `linear-gradient(160deg, ${color}70 0%, ${color}26 100%)`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                    boxShadow: isMoving ? `0 0 18px ${color}b8` : `0 0 10px ${color}45`,
                    transition: 'all 0.15s ease', flexShrink: 0, cursor: 'pointer',
                }}
            >
                <img
                    src={avatarSrc}
                    alt={player?.name || initial}
                    style={{
                        width: compact ? 38 : 46,
                        height: compact ? 38 : 46,
                        borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.55)',
                        objectFit: 'cover',
                        marginBottom: compact ? 2 : 4,
                        boxShadow: '0 4px 10px rgba(0,0,0,0.28)',
                    }}
                />
                <div style={{ display: 'flex', gap: 3 }}>
                    {[...Array(2)].map((_, i) => (
                        <div key={i} style={{ width: compact ? 7 : 8, height: compact ? 7 : 8, borderRadius: '50%', background: color, border: '1px solid rgba(255,255,255,0.5)' }} />
                    ))}
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: flip ? 'flex-end' : 'flex-start', gap: 2, paddingBottom: 4 }}>
                <span style={{ fontSize: compact ? 15 : 18 }}>🇮🇳</span>
                <div style={{ width: compact ? 11 : 14, height: compact ? 11 : 14, borderRadius: '50%', background: color, border: '2px solid white' }} />
            </div>
        </div>
    );
});

// ── Mute button ─────────────────────────────────────────────────────────────
const MuteButton = () => {
    const [muted, setMuted] = useState(soundManager.isMuted());
    const toggle = () => {
        const nowMuted = soundManager.toggleMute();
        setMuted(nowMuted);
    };
    return (
        <button
            title={muted ? 'Unmute sounds' : 'Mute sounds'}
            onClick={toggle}
            style={{
                width: 36, height: 36, borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.3)',
                background: muted ? 'rgba(255,60,60,0.25)' : 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', cursor: 'pointer', flexShrink: 0,
                fontSize: 16, padding: 0,
                transition: 'background 0.2s',
            }}
        >
            {muted ? '🔇' : '🔊'}
        </button>
    );
};

// ── Main component ─────────────────────────────────────────────────────────
const Gameboard = () => {
    const navigate = useNavigate();
    const socket = useContext(SocketContext);
    const context = useContext(PlayerDataContext);
    const [pawns, setPawns] = useState([]);
    const [playersBySeat, setPlayersBySeat] = useState({ A: null, B: null, C: null, D: null });
    const [viewLayout, setViewLayout] = useState([]);
    const [viewerIdentity, setViewerIdentity] = useState(() => ({
        playerId: context?.playerId || null,
        seat: context?.seat || null,
        color: context?.color || null,
    }));
    const [rolledNumber, setRolledNumber] = useSocketData('game:roll');
    const [lastAuthoritativeRoll, setLastAuthoritativeRoll] = useState(null);
    const [time, setTime] = useState();
    const [isReady, setIsReady] = useState();
    const [nowMoving, setNowMoving] = useState(false);
    const [started, setStarted] = useState(false);
    const [movingPlayer, setMovingPlayer] = useState(null);
    const [winner, setWinner] = useState(null);
    const [timerMinutes, setTimerMinutes] = useState(0);
    const [matchEndTime, setMatchEndTime] = useState(null);
    const [timerResult, setTimerResult] = useState(null);
    // null means the room mode has not been resolved yet. Never default to the
    // 4-player shell: that caused the visible four-player flash in 1v1 rooms.
    const [roomMode, setRoomMode] = useState(() =>
        normalizeRoomMode(context) ?? readStoredRoomMode(context?.roomId)
    );
    const roomModeResolved = roomMode !== null;
    const isTwoPlayerMode = roomMode === 2;
    const [voiceEnabled, setVoiceEnabled] = useState(true);
    const [videoEnabled, setVideoEnabled] = useState(false);
    const [chatEnabled, setChatEnabled] = useState(true);
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [windowSize, setWindowSize] = useState({ w: window.innerWidth, h: window.innerHeight });
    const [showVoiceChat, setShowVoiceChat] = useState(() => {
        try {
            const saved = window.localStorage.getItem(PREF_KEYS.showVoiceChat);
            return saved === null ? false : JSON.parse(saved) === true;
        } catch {
            return false;
        }
    });
    const [showVideoChat, setShowVideoChat] = useState(false);
    const [showChat, setShowChat] = useState(() => {
        try {
            const saved = window.localStorage.getItem(PREF_KEYS.showChat);
            return saved === null ? true : JSON.parse(saved) === true;
        } catch {
            return true;
        }
    });
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [micMuted, setMicMuted] = useState(() => {
        try {
            return JSON.parse(window.localStorage.getItem(PREF_KEYS.micMuted) || 'false') === true;
        } catch {
            return false;
        }
    });
    const [openPawnValues, setOpenPawnValues] = useState([1, 6]);
    const [showRulePopup, setShowRulePopup] = useState(false);
    const [roomCode, setRoomCode] = useState('');
    const [viewerIsCreator, setViewerIsCreator] = useState(false);
    const [gameMode, setGameMode] = useState('classic');
    const [floatingEmoji, setFloatingEmoji] = useState(null);
    const [redirectCountdown, setRedirectCountdown] = useState(null);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const redirectTimerRef = useRef(null);
    // Lives (hearts) per color: { red: 3, blue: 3, ... }
    const [livesMap, setLivesMap] = useState({});
    const [heartBroken, setHeartBroken] = useState(null); // color that just lost a life
    const moveSeqRef = useRef(0);
    // The server sends this before the legacy numeric dice event, so the
    // post-roll sequence is ready before a fast pawn tap can fire.
    useEffect(() => {
        if (!socket) return undefined;
        const handleRollState = state => {
            if (!state || typeof state !== 'object') return;
            const nextSeq = Number(state.moveSeq);
            const nextRoll = Number(state.rolledNumber);
            if (Number.isFinite(nextSeq)) moveSeqRef.current = nextSeq;
            if (Number.isInteger(nextRoll) && nextRoll >= 1 && nextRoll <= 6) {
                setLastAuthoritativeRoll(nextRoll);
                setRolledNumber(nextRoll);
            }
        };
        socket.on('game:rollState', handleRollState);
        return () => socket.off('game:rollState', handleRollState);
    }, [socket, setRolledNumber]);
    const prevRolledRef = useRef(null);
    const emojiTimerRef = useRef(null);
    const previousPlayersRef = useRef([]);

    useEffect(() => {
        const modeFromSession = normalizeRoomMode(context) ?? readStoredRoomMode(context?.roomId);
        if (modeFromSession !== null) setRoomMode(modeFromSession);
        setViewerIdentity(previous => ({
            // Fresh player:data from reconnect is authoritative. Retain the old
            // value only while context is temporarily incomplete.
            playerId: context?.playerId || previous.playerId || null,
            seat: context?.seat || previous.seat || null,
            color: context?.color || previous.color || null,
        }));
    }, [context]);

    // Request push notification permission once on mount
    useEffect(() => { pushNotifications.init(); }, []);

    // Preload dice faces + rolling gif so the first roll doesn't flash while
    // the browser fetches the image mid-animation.
    useEffect(() => {
        [...diceImages, rollingGif].forEach(src => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    useEffect(() => {
        // rAF-throttled: resize fires continuously while dragging a window edge
        let raf = null;
        const onResize = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = null;
                setWindowSize({ w: window.innerWidth, h: window.innerHeight });
            });
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    const isWideGameScreen = windowSize.w >= 960;
    const gameViewportWidth = useMemo(
        () => (isWideGameScreen ? Math.min(440, windowSize.w - 48) : windowSize.w),
        [isWideGameScreen, windowSize.w]
    );
    const gameViewportInset = useMemo(
        () => Math.max((windowSize.w - gameViewportWidth) / 2, 0),
        [windowSize.w, gameViewportWidth]
    );
    // The game is deliberately capped at 440px on wide monitors. HUD sizing
    // must follow that inner game viewport, not the full browser width.
    const compactGameViewport = gameViewportWidth <= 520;
    const gameTopOffset = compactGameViewport ? 62 : 68;
    const bottomHudHeight = compactGameViewport ? 116 : 120;
    const diceDockSize = compactGameViewport ? 72 : 86;
    const boardScale = useMemo(() => {
        const availH = windowSize.h - gameTopOffset - bottomHudHeight;
        const availW = gameViewportWidth - 16;
        const targetHeight = 600;
        return Math.max(Math.min(availH / targetHeight, availW / 460, isTwoPlayerMode ? 2.0 : 1.18), 0.45);
    }, [bottomHudHeight, gameTopOffset, gameViewportWidth, isTwoPlayerMode, windowSize.h]);

    const resolvedViewerPlayerId = viewerIdentity.playerId || context?.playerId || null;
    const resolvedViewerSeat = viewerIdentity.seat || context?.seat || null;
    const resolvedViewerColor = viewerIdentity.color || context?.color || null;
    const bottomSeat = useMemo(
        () => viewLayout.find(item => item?.position === 'B')?.seat || resolvedViewerSeat,
        [viewLayout, resolvedViewerSeat]
    );
    const topSeat = useMemo(
        () => viewLayout.find(item => item?.position === 'C')?.seat || null,
        [viewLayout]
    );

    const selfPlayer = useMemo(() => {
        if (!isTwoPlayerMode) return null;
        const twoPlayerList = Object.values(playersBySeat).filter(Boolean);
        const viewerId = resolvedViewerPlayerId?.toString?.() || String(resolvedViewerPlayerId || '');
        return twoPlayerList.find(player => (player?._id?.toString?.() || String(player?._id || '')) === viewerId)
            || twoPlayerList.find(player => resolvedViewerColor && player?.color === resolvedViewerColor)
            || twoPlayerList.find(player => bottomSeat && player?.seat === bottomSeat)
            || null;
    }, [isTwoPlayerMode, playersBySeat, resolvedViewerPlayerId, bottomSeat, resolvedViewerColor]);

    const opponentPlayer = useMemo(() => {
        if (!isTwoPlayerMode) return null;
        const twoPlayerList = Object.values(playersBySeat).filter(Boolean);
        const selfId = selfPlayer?._id?.toString?.() || String(selfPlayer?._id || '');
        return twoPlayerList.find(player => (player?._id?.toString?.() || String(player?._id || '')) !== selfId)
            || twoPlayerList.find(player => topSeat && player?.seat === topSeat)
            || null;
    }, [isTwoPlayerMode, playersBySeat, selfPlayer?._id, topSeat]);

    // Player-relative 2-player presentation, matching Ludo King:
    // every device sees itself as blue and the other player as green.
    // Logical/server colors remain untouched for moves, lives, winner checks, etc.
    const selfDisplayPlayer = useMemo(
        () => (selfPlayer ? {
            ...selfPlayer,
            logicalColor: selfPlayer.color,
            visibleRole: 'self',
            visibleKey: TWO_PLAYER_VISIBLE_COLORS.self.key,
            color: TWO_PLAYER_VISIBLE_COLORS.self.hex,
        } : null),
        [selfPlayer]
    );
    const opponentDisplayPlayer = useMemo(
        () => (opponentPlayer ? {
            ...opponentPlayer,
            logicalColor: opponentPlayer.color,
            visibleRole: 'opponent',
            visibleKey: TWO_PLAYER_VISIBLE_COLORS.opponent.key,
            color: TWO_PLAYER_VISIBLE_COLORS.opponent.hex,
        } : null),
        [opponentPlayer]
    );
    const getDisplayColor = useCallback((logicalColor) => {
        if (!isTwoPlayerMode || !logicalColor) return logicalColor;
        return getTwoPlayerVisibleHex(logicalColor, selfPlayer?.color || resolvedViewerColor);
    }, [isTwoPlayerMode, selfPlayer?.color, resolvedViewerColor]);
    const visiblePawns = useMemo(() => {
        const activePlayers = Object.values(playersBySeat).filter(Boolean);
        return getVisiblePawns(pawns, activePlayers, isTwoPlayerMode);
    }, [pawns, playersBySeat, isTwoPlayerMode]);
    const joinedPlayerCount = useMemo(
        () => Object.values(playersBySeat).filter(player => player && player.name !== '...').length,
        [playersBySeat]
    );
    const minimumStartPlayers = gameMode === 'team_2v2' ? 4 : 2;
    const canCreatorStart = viewerIsCreator && joinedPlayerCount >= minimumStartPlayers;

    useEffect(() => {
        try { window.localStorage.setItem(PREF_KEYS.showChat, JSON.stringify(showChat)); } catch {}
    }, [showChat]);
    useEffect(() => {
        try { window.localStorage.setItem(PREF_KEYS.showVoiceChat, JSON.stringify(showVoiceChat)); } catch {}
    }, [showVoiceChat]);
    useEffect(() => {
        try { window.localStorage.setItem(PREF_KEYS.micMuted, JSON.stringify(micMuted)); } catch {}
    }, [micMuted]);

    const persistActiveSession = useCallback((roomData) => {
        try {
            const winnerInfo = roomData?.winner
                ? (roomData.players || []).find(p => p.color === roomData.winner)
                : null;
            const viewerPlayer = (roomData?.players || []).find(player =>
                (roomData?.viewerSeat && player?.seat === roomData.viewerSeat)
                || (roomData?.viewerColor && player?.color === roomData.viewerColor)
            );
            const snapshot = {
                roomId: roomData?._id?.toString() || context?.roomId,
                playerId: viewerPlayer?._id?.toString?.() || context?.playerId || null,
                color: roomData?.viewerColor || viewerPlayer?.color || context?.color || null,
                seat: roomData?.viewerSeat || viewerPlayer?.seat || context?.seat || null,
                maxPlayers: Number(roomData?.maxPlayers) || normalizeRoomMode(roomData) || roomMode || null,
                layoutMode: roomData?.layoutMode || (normalizeRoomMode(roomData) === 2 ? 'two-player' : normalizeRoomMode(roomData) === 3 ? 'three-player' : roomMode === 4 ? 'standard' : null),
                roomName: roomData?.name || 'Game Room',
                roomCode: roomData?.roomCode || null,
                gameMode: roomData?.gameMode || 'classic',
                timerMinutes: Number(roomData?.timerMinutes || 0),
                matchEndTime: roomData?.matchEndTime || null,
                timerResult: roomData?.timerResult || null,
                status: roomData?.winner ? 'completed' : roomData?.started ? 'running' : 'waiting',
                winner: roomData?.winner || null,
                winnerName: winnerInfo?.name || null,
                updatedAt: Date.now(),
            };
            window.localStorage.setItem('ludoActiveSession', JSON.stringify(snapshot));
            if (snapshot.status === 'completed') {
                window.localStorage.setItem('ludoLastGameResult', JSON.stringify({ ...snapshot, completedAt: Date.now() }));
            }
        } catch {}
    }, [context?.playerId, context?.color, context?.seat, context?.roomId, roomMode]);

    const buildSeatMap = useCallback((roomPlayers, isTwoPlayer, maxPlayers) => {
        const seatMap = { A: null, B: null, C: null, D: null };
        if (!roomPlayers?.length) return seatMap;

        // Determine number of slots: if isTwoPlayer, use 2; otherwise use maxPlayers (default 4)
        const numSlots = isTwoPlayer ? 2 : (maxPlayers && maxPlayers > 2 ? maxPlayers : 4);

        if (isTwoPlayer) {
            // For 2-player: use actual seats (B and D typically, or whatever is set)
            roomPlayers.forEach(player => {
                const s = player.seat;
                if (s && Object.prototype.hasOwnProperty.call(seatMap, s)) seatMap[s] = player;
            });
            return seatMap;
        }
        // For 3 or 4 player: show slots up to maxPlayers
        const slots = ['A', 'B', 'C', 'D'].slice(0, numSlots);
        slots.forEach((seat, i) => {
            const player = roomPlayers[i];
            seatMap[seat] = player ? { ...player, seat } : { seat, name: '...', ready: false };
        });
        return seatMap;
    }, []);

    // ── Sound: play dice sound when rolledNumber changes ──────────────────
    useEffect(() => {
        if (rolledNumber !== null && rolledNumber !== undefined && rolledNumber !== prevRolledRef.current) {
            soundManager.playDice();
            if (rolledNumber === 6) {
                setTimeout(() => soundManager.playSix(), 350);
                toastManager.info('🎲 Six! Roll again!');
            }
            prevRolledRef.current = rolledNumber;
        }
    }, [rolledNumber]);

    // WebSocket state is server-pushed. Keep only a throttled one-shot resync for
    // first mount, reconnect, and returning to a backgrounded tab.
    const lastRoomDataRequestAtRef = useRef(0);
    const lastRoomDataRef = useRef(null);

    useEffect(() => {
        const requestRoomData = () => {
            if (!socket?.connected || !context?.roomId) return;
            const now = Date.now();
            if (now - lastRoomDataRequestAtRef.current < 500) return;
            lastRoomDataRequestAtRef.current = now;
            socket.emit('room:data', context.roomId);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') requestRoomData();
        };

        // Initial snapshot plus one-shot recovery after reconnect/tab resume.
        // All normal dice, pawn, turn, join and winner updates arrive via
        // Socket.IO pushes; there is no repeated database polling anymore.
        requestRoomData();
        socket.on('connect', requestRoomData);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', requestRoomData);

        const handleRoomData = data => {
            try {
                const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
                // Skip if no players
                if (parsedData.players == null) return;
                // Build a key that captures EVERYTHING that can change so we never skip a real update
                const movingId = (parsedData.players.find(p => p?.nowMoving === true) || {})._id || '';
                const playerKey = parsedData.players
                    .map(p => `${p?._id}:${p?.ready ? 1 : 0}:${p?.lives}`)
                    .join('|');
                const pawnKey = (parsedData.pawns || []).map(p => `${p?._id}:${p?.position}:${p?.color}`).join(',');
                const layoutKey = (parsedData.viewLayout || []).map(item => `${item?.seat}:${item?.position}`).join(',');
                const ruleKey = normalizeOpenPawnValues(parsedData.openPawnValues).join(',');
                const dataKey = `${parsedData.started}:${parsedData.winner}:${parsedData.rolledNumber}:${parsedData.moveSeq}:${movingId}:${parsedData.players.length}:${playerKey}:${pawnKey}:${parsedData.layoutMode}:${parsedData.maxPlayers}:${parsedData.viewerSeat}:${parsedData.viewerColor}:${layoutKey}:${parsedData.voiceEnabled}:${parsedData.videoEnabled}:${parsedData.chatEnabled}:${parsedData.gameMode}:${parsedData.timerMinutes}:${parsedData.matchEndTime}:${parsedData.endedBy}:${JSON.stringify(parsedData.timerResult || null)}:${ruleKey}`;
                if (lastRoomDataRef.current === dataKey) return;
                lastRoomDataRef.current = dataKey;
                const roomPlayers = Array.isArray(parsedData.players) ? [...parsedData.players] : [];
                const previousPlayers = previousPlayersRef.current;
                const previousIds = new Set(previousPlayers.map(player => player?._id).filter(Boolean));
                const currentIds = new Set(roomPlayers.map(player => player?._id).filter(Boolean));
                const joinedPlayers = roomPlayers.filter(player => player?._id && !previousIds.has(player._id));
                const leftPlayers = previousPlayers.filter(player => player?._id && !currentIds.has(player._id));

                // Resolve mode only from explicit authoritative metadata. Values may arrive
                // as strings, so normalize before comparing. If a transient packet omits
                // mode metadata, retain the previously resolved mode instead of falling
                // back to four players and flickering the entire UI.
                const explicitRoomMode = normalizeRoomMode(parsedData);
                const inferredRoomMode = explicitRoomMode ?? (
                    Array.isArray(parsedData.viewLayout) && parsedData.viewLayout.length === 2
                        ? 2
                        : null
                );
                setRoomMode(previousMode => inferredRoomMode ?? previousMode);
                const effectiveRoomMode = inferredRoomMode ?? roomMode ?? normalizeRoomMode(context);
                const maxPlayersValue = effectiveRoomMode || Number(parsedData.maxPlayers) || 4;
                const isTwoPlayer = effectiveRoomMode === 2;
                setVoiceEnabled(Boolean(parsedData.voiceEnabled));
                setVideoEnabled(Boolean(parsedData.videoEnabled));
                setChatEnabled(parsedData.chatEnabled !== false);
                if (parsedData.chatEnabled === false) setShowChat(false);
                if (parsedData.voiceEnabled === false) setShowVoiceChat(false);
                if (parsedData.redirectUrl) setRedirectUrl(parsedData.redirectUrl);
                if (parsedData.roomCode) setRoomCode(parsedData.roomCode);
                setViewerIsCreator(parsedData.viewerIsCreator === true);
                setGameMode(parsedData.gameMode || 'classic');
                setTimerMinutes(Number(parsedData.timerMinutes || 0));
                setMatchEndTime(parsedData.matchEndTime || null);
                if (parsedData.timerResult) setTimerResult(parsedData.timerResult);
                setOpenPawnValues(normalizeOpenPawnValues(parsedData.openPawnValues));
                setPlayersBySeat(buildSeatMap(roomPlayers, isTwoPlayer, maxPlayersValue));
                setViewLayout(parsedData.viewLayout || []);
                setViewerIdentity(previous => ({
                    playerId: context?.playerId || previous.playerId,
                    seat: parsedData.viewerSeat || context?.seat || previous.seat,
                    color: parsedData.viewerColor || context?.color || previous.color,
                }));
                persistActiveSession(parsedData);
                const nowMovingPlayer = roomPlayers.find(p => p.nowMoving === true);
                if (nowMovingPlayer && context?.playerId) {
                    // Ensure both IDs are strings for comparison (handle ObjectId/string mismatch)
                    const nowMovingId = typeof nowMovingPlayer._id === 'string' ? nowMovingPlayer._id : nowMovingPlayer._id?.toString?.();
                    const contextId = typeof context.playerId === 'string' ? context.playerId : context.playerId?.toString?.();
                    const isMyTurn = nowMovingId && contextId && nowMovingId === contextId;
                    setNowMoving(isMyTurn);
                    setMovingPlayer(nowMovingPlayer.color);
                } else {
                    setNowMoving(false);
                    setMovingPlayer(nowMovingPlayer?.color || null);
                }
                const currentPlayer = roomPlayers.find(p => {
                    const pId = typeof p._id === 'string' ? p._id : p._id?.toString?.();
                    const cId = typeof context.playerId === 'string' ? context.playerId : context.playerId?.toString?.();
                    return pId === cId;
                });
                if (currentPlayer) setIsReady(currentPlayer.ready);
                setRolledNumber(parsedData.rolledNumber);
                // Never replace a populated board with an empty pawn list — a
                // malformed payload would swap the whole board for the loading
                // spinner and then back (visible blink / "stuck" screen).
                setPawns(prev => (Array.isArray(parsedData.pawns) && parsedData.pawns.length ? parsedData.pawns : prev));
                setTime(parsedData.nextMoveTime);
                // ChatLog sent via separate event if available
                if (Array.isArray(parsedData.chatLog)) {
                    setChatMessages(
                        parsedData.chatLog.map(msg => ({
                            ...msg,
                            self: msg?.color === context?.color && msg?.name === context?.name,
                        }))
                    );
                }
                setStarted(parsedData.started);
                setWinner(parsedData.winner || null);
                moveSeqRef.current = Number.isFinite(parsedData.moveSeq) ? parsedData.moveSeq : 0;
                // Update lives map from player data
                const newLives = {};
                roomPlayers.forEach(p => { if (p.color) newLives[p.color] = p.lives != null ? p.lives : 3; });
                setLivesMap(newLives);

                if (previousPlayers.length > 0) {
                    const contextId = typeof context?.playerId === 'string' ? context.playerId : context?.playerId?.toString?.();
                    joinedPlayers
                        .filter(player => {
                            const pId = typeof player._id === 'string' ? player._id : player._id?.toString?.();
                            return pId !== contextId;
                        })
                        .forEach(player => {
                            soundManager.playJoin();
                            toastManager.info(`👋 ${player.name || 'A player'} joined`);
                            pushNotifications.playerJoined(player.name);
                        });

                    leftPlayers
                        .filter(player => {
                            const pId = typeof player._id === 'string' ? player._id : player._id?.toString?.();
                            return pId !== contextId;
                        })
                        .forEach(player => {
                            soundManager.playLeave();
                            toastManager.warning(`🚪 ${player.name || 'A player'} left`);
                            pushNotifications.playerLeft(player.name);
                        });
                }

                previousPlayersRef.current = roomPlayers;
            } catch (err) {
                console.error('Error processing room data:', err);
            }
        };

        const handleWinner = w => {
            const winnerData = normalizeWinnerEvent(w);
            if (!winnerData?.color) return;
            setWinner(winnerData.color);
            soundManager.playWin();
            celebrateBurst();
            setTimeout(() => celebrateBurst(), 700);
            const isMe = winnerData.color === context?.color || winnerData.isCurrentPlayer;
            if (isMe) {
                toastManager.success('🏆 Congratulations! You won!');
                pushNotifications.youWon();
            } else {
                toastManager.info(`🏆 ${winnerData.name || winnerData.color || 'Someone'} won the game`);
                pushNotifications.someoneWon(winnerData.name || winnerData.color);
            }
        };

        const handleChat = d => {
            try {
                const msg = typeof d === 'string' ? JSON.parse(d) : d;
                setChatMessages(prev => [...prev.slice(-99), {
                    ...msg,
                    self: msg?.color === context?.color && msg?.name === context?.name,
                }]);
            } catch {}
        };

        socket.on('game:chat', handleChat);

        const handlePawnMoved = (data) => {
            soundManager.playMove();
            // data may have: { killed: true, killerName, victimName, safe: true, home: true }
            if (data?.killed) {
                soundManager.playKill();
                const killerName = data.killerName || 'A player';
                const victimName = data.victimName || 'opponent';
                pushNotifications.tokenKilled(killerName, victimName);
                toastManager.warning(`💀 ${killerName} killed ${victimName}'s token!`);
            }
            if (data?.safe) {
                soundManager.playSafe();
                toastManager.info('🛡️ Token reached safe zone!');
            }
            if (data?.home) {
                soundManager.playHome();
                toastManager.success('🏠 Token made it home!');
                pushNotifications.tokenHome(data.playerName);
            }
        };

        const handleTimerEnded = payload => {
            const result = typeof payload === 'string' ? JSON.parse(payload) : payload;
            if (!result || typeof result !== 'object') return;
            setTimerResult(result);
            if (result.winner) setWinner(result.winner);
            toastManager.info(`⏱ Time finished • ${describeTimerDecision(result.decidedBy)}`);
        };
        const handleRedirect = () => window.location.reload();

        socket.on('room:data', handleRoomData);
        socket.on('game:winner', handleWinner);
        socket.on('game:timerEnded', handleTimerEnded);
        socket.on('game:pawnMoved', handlePawnMoved);
        socket.on('redirect', handleRedirect);

        return () => {
            socket.off('connect', requestRoomData);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', requestRoomData);
            socket.off('room:data', handleRoomData);
            socket.off('game:winner', handleWinner);
            socket.off('game:timerEnded', handleTimerEnded);
            socket.off('game:pawnMoved', handlePawnMoved);
            socket.off('redirect', handleRedirect);
            socket.off('game:chat', handleChat);
        };
    }, [socket, context, roomMode, setRolledNumber, buildSeatMap, persistActiveSession]);

    useEffect(() => {
        if (!voiceEnabled) {
            setShowVoiceChat(false);
            return;
        }
        try {
            const saved = window.localStorage.getItem(PREF_KEYS.showVoiceChat);
            if (saved !== null) {
                setShowVoiceChat(JSON.parse(saved) === true);
            }
        } catch {
            setShowVoiceChat(false);
        }
    }, [voiceEnabled]);

    useEffect(() => {
        if (!context?.roomId) return;
        const ruleKey = `ludo_open_rule_seen_${context.roomId}`;
        try {
            const seen = window.sessionStorage.getItem(ruleKey);
            if (!seen) {
                setShowRulePopup(true);
                window.sessionStorage.setItem(ruleKey, JSON.stringify(normalizeOpenPawnValues(openPawnValues)));
            }
        } catch {
            setShowRulePopup(true);
        }
    }, [context?.roomId, openPawnValues]);

    useEffect(() => {
        if (!socket) return;
        const handleYourTurn = () => {
            soundManager.playYourTurn();
            toastManager.info("🎲 It's your turn!");
            pushNotifications.yourTurn(context?.name);
        };
        const handleGameStarted = () => {
            toastManager.success('🎮 Game started!');
            pushNotifications.gameStarted();
        };
        const handleRolledSix = d => {
            pushNotifications.rolledSix(d?.name);
        };
        const handleLifeLost = (data) => {
            try {
                const d = typeof data === 'string' ? JSON.parse(data) : data;
                if (!d?.color) return;
                setLivesMap(prev => ({ ...prev, [d.color]: d.livesLeft }));
                setHeartBroken(d.color);
                setTimeout(() => setHeartBroken(null), 1000);
                toastManager.warning(`💔 ${d.color} lost a life! ${d.livesLeft} remaining`);
            } catch {}
        };
        socket.on('game:yourTurn', handleYourTurn);
        socket.on('game:started', handleGameStarted);
        socket.on('game:six', handleRolledSix);
        socket.on('game:life_lost', handleLifeLost);
        return () => {
            socket.off('game:yourTurn', handleYourTurn);
            socket.off('game:started', handleGameStarted);
            socket.off('game:six', handleRolledSix);
            socket.off('game:life_lost', handleLifeLost);
        };
    }, [socket, context?.name, context?.color]);

    const handleSendChat = useCallback((text) => {
        if (!socket || !text.trim()) return;
        socket.emit('game:chat', { text: text.trim(), roomId: context?.roomId });
        setChatInput('');
    }, [socket, context?.roomId]);

    const handleCopyRoom = () => {
        try {
            const code = roomCode || context?.roomId || '';
            if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(code);
            else { const ta = document.createElement('textarea'); ta.value = code; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
            toastManager.success('Room code copied!');
        } catch {}
    };

    const showFloatingEmoji = useCallback((emoji) => {
        setFloatingEmoji(emoji);
        if (emojiTimerRef.current) clearTimeout(emojiTimerRef.current);
        emojiTimerRef.current = setTimeout(() => setFloatingEmoji(null), 2500);
    }, []);

    const handleSendEmoji = useCallback((emoji) => {
        if (!socket) return;
        socket.emit('game:emoji', { emoji, roomId: context?.roomId });
        showFloatingEmoji(emoji);
        toastManager.info(`You sent: ${emoji}`);
    }, [socket, context?.roomId, showFloatingEmoji]);

    // Listen for emoji from other players
    useEffect(() => {
        if (!socket) return;
        const handleIncomingEmoji = (data) => {
            showFloatingEmoji(data?.emoji || '😊');
            toastManager.info(`${data?.name || 'Opponent'} reacted: ${data?.emoji || ''}`);
        };
        socket.on('game:emoji', handleIncomingEmoji);
        return () => socket.off('game:emoji', handleIncomingEmoji);
    }, [socket, showFloatingEmoji]);

    // Redirect countdown after winner (optional redirectUrl)
    useEffect(() => {
        if (!winner || !redirectUrl) return;
        setRedirectCountdown(60);
        redirectTimerRef.current = setInterval(() => {
            setRedirectCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(redirectTimerRef.current);
                    window.location.href = redirectUrl;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(redirectTimerRef.current);
    }, [winner, redirectUrl]);

    const pawnsReady = pawns.length >= 16;
    const ended = winner !== null;
    const diceProps = { rolledNumber, setRolledNumber, nowMoving, movingPlayer, moveSeqRef };
    const selfMoving = Boolean(selfPlayer?.nowMoving);
    const opponentMoving = Boolean(opponentPlayer?.nowMoving);
    const viewportFrameStyle = isWideGameScreen
        ? {
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
        }
        : null;
    const viewportChromeStyle = isWideGameScreen
        ? {
            width: gameViewportWidth,
            height: '100%',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            borderRight: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.03), 0 20px 80px rgba(0,0,0,0.45)',
            background: 'rgba(0,0,0,0.08)',
        }
        : null;

    // ── Fixed top bar ───────────────────────────────────────────────────────
    const topBar = (
        <div
            className={`${styles.gameTopBar} fixed top-4 z-40`}
            style={{
                left: gameViewportInset + 10,
                right: gameViewportInset + 10,
                maxWidth: Math.max(gameViewportWidth - 20, 0),
            }}
        >
            <div className={`${styles.gameTopBarInner} ${compactGameViewport ? styles.compactGameTopBar : ''} flex items-center gap-2 rounded-2xl backdrop-blur bg-white/10 border border-white/15 shadow-soft px-3 py-2`}>
                <TopIconButton title="Leave game" onClick={() => setShowLeaveConfirm(true)} danger>
                    <ExitGlyph />
                </TopIconButton>
                <div className="flex-1 min-w-0">
                    <button
                        onClick={handleCopyRoom}
                        className="w-full rounded-xl text-white/90 px-3 py-1.5 text-xs md:text-sm text-left truncate"
                        style={{
                            letterSpacing: '0.04em',
                            background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.12)',
                            boxShadow: 'none',
                        }}
                    >
                        <span style={{ fontWeight: 800, fontSize: 15, color: '#facc15', fontFamily: 'monospace', letterSpacing: '0.15em' }}>
                            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.48)', letterSpacing: '0.08em', marginRight: 7 }}>ROOM ID</span>
                            {roomCode || String(context?.roomId || '—').slice(-8).toUpperCase()}
                        </span>
                        <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.4)', display: 'inline-flex', verticalAlign: 'middle' }}>
                            <CopyGlyph />
                        </span>
                    </button>
                </div>
                {started && !ended && gameMode === 'timer' && <MatchTimer endTime={matchEndTime} minutes={timerMinutes} />}
                {started && !ended && gameMode !== 'timer' && <TurnTimer time={time} />}
                <BoardModeTabs
                    activeMode="2d"
                    onSelect2D={() => {}}
                    onSelect3D={() => navigate('/play3d')}
                />
                <MuteButton />
                {chatEnabled && (
                    <TopIconButton title={showChat ? 'Close chat' : 'Open chat'} onClick={() => setShowChat(v => !v)} active={showChat}>
                        💬
                        {chatMessages.length > 0 && !showChat && (
                            <span style={{ position: 'absolute', top: -3, right: -3, width: 14, height: 14, borderRadius: '50%', background: '#00d4ff', fontSize: 8, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000' }}>
                                {chatMessages.length > 9 ? '9+' : chatMessages.length}
                            </span>
                        )}
                    </TopIconButton>
                )}
                <TopIconButton
                    title={
                        !voiceEnabled
                            ? 'Voice chat disabled for this room'
                            : showVoiceChat
                                ? (micMuted ? 'Unmute mic' : 'Mute mic')
                                : 'Join voice chat'
                    }
                    onClick={() => {
                        if (!voiceEnabled) {
                            toastManager.warning('Voice chat is disabled for this room.');
                            return;
                        }
                        if (!showVoiceChat) {
                            setMicMuted(false);
                            setShowVoiceChat(true);
                            return;
                        }
                        setMicMuted(m => !m);
                    }}
                    active={voiceEnabled && showVoiceChat && !micMuted}
                    danger={voiceEnabled && showVoiceChat && micMuted}
                    disabled={false}
                >
                    <MicGlyph muted={voiceEnabled && showVoiceChat && micMuted} active={voiceEnabled && showVoiceChat && !micMuted} />
                </TopIconButton>
                {videoEnabled && (
                    <TopIconButton title="Video" onClick={() => setShowVideoChat(v => !v)} active={showVideoChat}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="14" height="14" rx="2"/><polygon points="22 7 16 12 22 17 22 7"/></svg>
                    </TopIconButton>
                )}
            </div>
        </div>
    );

    // ── Winner overlay ──────────────────────────────────────────────────────
    const winnerOverlay = winner ? (
        <Overlay>
            <div className={styles.winnerContainer} style={{ animation: 'winnerPop 0.5s cubic-bezier(0.34,1.56,0.64,1)' }}>
                <img src={trophyImage} alt='winner' style={{ animation: 'trophySpin 1.2s ease-in-out' }} />
                <h1>
                    {(() => {
                        const wp = Object.values(playersBySeat).find(p => p?.color === winner);
                        const isMe = winner === context?.color;
                        return (<>
                            {isMe ? '🏆 You Won!' : `${wp?.name || winner} wins!`}
                            <br />
                            <span style={{ color: getDisplayColor(winner), fontSize: '0.75em' }}>{getDisplayColor(winner)}</span>
                        </>);
                    })()}
                </h1>
                {timerResult?.standings?.length > 0 && (
                    <div style={{ width:'min(88vw,360px)', margin:'4px auto 14px', padding:'12px 14px', borderRadius:14, background:'rgba(6,182,212,0.10)', border:'1px solid rgba(103,232,249,0.30)' }}>
                        <div style={{ color:'#a5f3fc', fontSize:11, fontWeight:900, letterSpacing:'0.08em', marginBottom:8 }}>
                            ⏱ {describeTimerDecision(timerResult.decidedBy)}
                        </div>
                        {timerResult.standings.map((score, index) => (
                            <div key={score.color || index} style={{ display:'grid', gridTemplateColumns:'28px 1fr auto', gap:8, alignItems:'center', padding:'6px 0', borderTop:index ? '1px solid rgba(255,255,255,0.08)' : 'none', color:score.color === winner ? '#facc15' : 'rgba(255,255,255,0.78)', fontSize:12 }}>
                                <strong>#{index + 1}</strong>
                                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{score.name || score.color}</span>
                                <strong>{score.totalProgress} total • max {score.maxPawnProgress}</strong>
                            </div>
                        ))}
                    </div>
                )}
                <button onClick={() => socket.emit('player:exit')}>Play Again</button>
                {redirectUrl && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <a
                            href={redirectUrl}
                            style={{ display: 'inline-block', padding: '10px 22px', borderRadius: 10, background: 'linear-gradient(135deg,#00d4ff,#0090ff)', color: '#fff', fontWeight: 800, fontSize: 14, textDecoration: 'none', boxShadow: '0 4px 16px rgba(0,212,255,0.4)' }}
                        >
                            🌐 Go to Website
                        </a>
                        {redirectCountdown !== null && redirectCountdown > 0 && (
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                                Auto-redirect in {redirectCountdown}s ·{' '}
                                <span style={{ color: '#ff6b6b', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { clearInterval(redirectTimerRef.current); setRedirectCountdown(null); }}>
                                    Cancel
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Overlay>
    ) : null;

    const chatPanel = showChat && chatEnabled ? (
        <div className={styles.chatPanel} style={{
            position: 'fixed', bottom: isTwoPlayerMode ? 130 : 80, right: gameViewportInset + 10,
            width: Math.min(300, windowSize.w - 24), maxHeight: 360,
            background: 'rgba(6,10,30,0.97)', backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0,212,255,0.25)', borderRadius: 16,
            display: 'flex', flexDirection: 'column', zIndex: 200,
            boxShadow: '0 8px 40px rgba(0,0,0,0.7)',
        }}>
            {/* header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span style={{ fontWeight: 800, fontSize: 13, color: '#00d4ff' }}>💬 Chat</span>
                <button onClick={() => setShowChat(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', padding: 0, fontSize: 16, cursor: 'pointer', width: 'auto', height: 'auto', boxShadow: 'none' }}>✕</button>
            </div>
            {/* messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0 }}>
                {chatMessages.length === 0 && (
                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', marginTop: 16 }}>No messages yet</div>
                )}
                {chatMessages.map((msg, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.self ? 'flex-end' : 'flex-start' }}>
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>{msg.name}</span>
                        <div style={{
                            padding: '6px 10px', borderRadius: 10, maxWidth: '85%', fontSize: 13, lineHeight: 1.4,
                            background: msg.self ? 'rgba(0,212,255,0.18)' : 'rgba(255,255,255,0.08)',
                            color: '#fff', border: `1px solid ${msg.self ? 'rgba(0,212,255,0.35)' : 'rgba(255,255,255,0.1)'}`,
                            borderBottomRightRadius: msg.self ? 2 : 10,
                            borderBottomLeftRadius:  msg.self ? 10 : 2,
                        }}>{msg.text}</div>
                    </div>
                ))}
            </div>
            {/* input */}
            <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 6 }}>
                <input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { handleSendChat(chatInput); } }}
                    placeholder="Type message…"
                    style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: 13, outline: 'none' }}
                />
                <button
                    onClick={() => handleSendChat(chatInput)}
                    style={{ padding: '7px 12px', borderRadius: 8, background: 'linear-gradient(135deg,#00d4ff,#0090ff)', color: '#000', fontWeight: 800, fontSize: 12, border: 'none', cursor: 'pointer', boxShadow: 'none', width: 'auto', height: 'auto' }}
                >▶</button>
            </div>
        </div>
    ) : null;

    const rtcOverlays = (
        <>
            {showVoiceChat && voiceEnabled && <VoiceChat video={false} muted={micMuted} onClose={() => { setShowVoiceChat(false); }} position="bottom-right" />}
            {showVideoChat && videoEnabled && <VoiceChat video={true} onClose={() => setShowVideoChat(false)} position="bottom-left" />}
        </>
    );

    const ruleOverlay = showRulePopup ? (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 210,
            background: 'rgba(4,8,24,0.68)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
        }}>
            <div style={{
                width: 'min(92vw, 420px)',
                background: 'linear-gradient(180deg, rgba(12,18,48,0.98), rgba(7,12,32,0.98))',
                border: '1px solid rgba(0,212,255,0.24)',
                borderRadius: 24,
                boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
                padding: '22px 22px 18px',
                color: '#fff',
            }}>
                <div style={{ fontSize: 26, marginBottom: 8 }}>🎲 Pawn Rule</div>
                <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>{describeOpenPawnRule(openPawnValues)}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.6 }}>
                    You need {normalizeOpenPawnValues(openPawnValues).join(' or ')} to open a pawn from base in this room.
                </div>
                {roomCode && (
                    <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 14, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 1 }}>Room ID</div>
                        <div style={{ marginTop: 4, fontSize: 18, fontWeight: 800, color: '#ffe14a', letterSpacing: 2 }}>{roomCode}</div>
                    </div>
                )}
                <button
                    onClick={() => setShowRulePopup(false)}
                    style={{
                        marginTop: 16,
                        width: '100%',
                        height: 46,
                        borderRadius: 14,
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 800,
                        background: 'linear-gradient(135deg,#00d4ff,#0090e7)',
                        color: '#05101f',
                    }}
                >
                    Start Playing
                </button>
            </div>
        </div>
    ) : null;

    // ── Loading ─────────────────────────────────────────────────────────────
    if (!pawnsReady) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <LoadingSpinner color="white" height={200} width={200} />
            </div>
        );
    }

    // ══════════════════════════════════════════════════════════════════════════
    // 2-PLAYER: LUDO KING LAYOUT
    // ══════════════════════════════════════════════════════════════════════════
    if (!roomModeResolved) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#07113a',
                color: '#fff',
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <LoadingSpinner />
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.62)', fontWeight: 700, letterSpacing: '0.05em' }}>
                        Loading room layout…
                    </div>
                </div>
            </div>
        );
    }

    if (isTwoPlayerMode) {
        return (
            <>
                {viewportFrameStyle && <div style={viewportFrameStyle}><div style={viewportChromeStyle} /></div>}
                {topBar}

                {showLeaveConfirm && (
                    <Overlay>
                        <div className={styles.confirmCard}>
                            <div className={styles.confirmIcon}>↩</div>
                            <h2>Leave game?</h2>
                            <p>Your current game session will close. You can rejoin only if the room still allows it.</p>
                            <div className={styles.confirmActions}>
                                <button type="button" className={styles.confirmGhost} onClick={() => setShowLeaveConfirm(false)}>Cancel</button>
                                <button
                                    type="button"
                                    className={styles.confirmDanger}
                                    onClick={() => {
                                        setShowLeaveConfirm(false);
                                        socket.emit('player:exit');
                                    }}
                                >
                                    Exit
                                </button>
                            </div>
                        </div>
                    </Overlay>
                )}

                {/* Top Players for 4-player mode */}
                {!isTwoPlayerMode && started && (
                    <div style={{
                        position: 'fixed',
                        top: 68,
                        left: gameViewportInset,
                        right: gameViewportInset,
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingLeft: 16,
                        paddingRight: 16,
                        zIndex: 20,
                        gap: 20,
                    }}>
                        {/* Top-Left Player (C) */}
                        {playersBySeat?.C && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 6,
                                minWidth: 0,
                                maxWidth: 120,
                            }}>
                                <div style={{
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.8)',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                }}>
                                    {playersBySeat.C?.name || 'Player C'}
                                </div>
                                {started && (
                                    <HeartsDisplay lives={livesMap[playersBySeat.C?.color]} />
                                )}
                                <div style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    background: playersBySeat.C?.color || '#888',
                                    border: playersBySeat.C?.nowMoving ? '2.5px solid #42a5f5' : '1.5px solid rgba(255,255,255,0.4)',
                                    boxShadow: playersBySeat.C?.nowMoving ? '0 0 12px rgba(66,165,245,0.6)' : 'none',
                                }}/>
                            </div>
                        )}

                        {/* Spacer */}
                        <div style={{ flex: 1 }} />

                        {/* Top-Right Player (A) */}
                        {playersBySeat?.A && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 6,
                                minWidth: 0,
                                maxWidth: 120,
                            }}>
                                <div style={{
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.8)',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                }}>
                                    {playersBySeat.A?.name || 'Player A'}
                                </div>
                                {started && (
                                    <HeartsDisplay lives={livesMap[playersBySeat.A?.color]} />
                                )}
                                <div style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    background: playersBySeat.A?.color || '#888',
                                    border: playersBySeat.A?.nowMoving ? '2.5px solid #42a5f5' : '1.5px solid rgba(255,255,255,0.4)',
                                    boxShadow: playersBySeat.A?.nowMoving ? '0 0 12px rgba(66,165,245,0.6)' : 'none',
                                }}/>
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.twoPlayerBoardArea} style={{
                    position: 'fixed',
                    top: !isTwoPlayerMode && started ? 168 : gameTopOffset,
                    left: gameViewportInset,
                    right: gameViewportInset,
                    bottom: bottomHudHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}>
                    <div style={{ transform: `scale(${boardScale})`, transformOrigin: 'center center', position: 'relative' }}>
                        <div className='container'>
                            <Map pawns={visiblePawns} nowMoving={nowMoving} rolledNumber={rolledNumber} moveSeqRef={moveSeqRef} openPawnValues={openPawnValues} isTwoPlayerMode={isTwoPlayerMode} viewerColor={selfPlayer?.color || resolvedViewerColor} />
                        </div>
                    </div>
                </div>

                {/* ── BOTTOM HUD ─────────────────────────────────────────── */}
                <div
                    className={styles.twoPlayerBottomBar}
                    data-testid="two-player-hud"
                    data-room-mode="two-player"
                    data-rolled-number={rolledNumber ?? ''}
                    data-last-roll-number={lastAuthoritativeRoll ?? ''}
                    data-moving-player={movingPlayer || ''}
                    style={{
                    position: 'fixed', bottom: 0,
                    left: gameViewportInset, right: gameViewportInset,
                    height: bottomHudHeight, zIndex: 30,
                    background: 'linear-gradient(180deg, rgba(8,14,48,0) 0%, #0d1b5e 18%, #111b52 100%)',
                    borderTop: '1.5px solid rgba(100,120,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    paddingLeft: compactGameViewport ? 8 : 12, paddingRight: compactGameViewport ? 8 : 12, gap: 0,
                }}>
                    <div className={styles.hudPlayerColumn} data-player-role="self" data-display-color="blue" data-visible-hex={TWO_PLAYER_VISIBLE_COLORS.self.hex} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: compactGameViewport ? 2 : 3, minWidth: 0, maxWidth: compactGameViewport ? 112 : 140 }}>
                        <PlayerAvatar player={selfDisplayPlayer} isMoving={selfMoving} flip={false} onSendEmoji={handleSendEmoji} compact={compactGameViewport} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 2 }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: selfDisplayPlayer?.color || '#888', border: '1.5px solid white', flexShrink: 0 }} />
                            <div style={{ color: 'white', fontWeight: 700, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 80 }}>
                                {selfPlayer?.name || 'You'}
                            </div>
                        </div>
                        {started && (
                            <div style={{ paddingLeft: 2 }}>
                                <HeartsDisplay
                                    lives={livesMap[selfPlayer?.color]}
                                    broken={heartBroken === selfPlayer?.color}
                                    compact={compactGameViewport}
                                />
                            </div>
                        )}
                        {started && selfMoving && <div style={{ color: '#4ade80', fontSize: 10, paddingLeft: 2 }}>▶ Your turn</div>}
                    </div>

                    {!started ? (
                        <WaitingDiceBox compact={compactGameViewport} isCreator={viewerIsCreator} canStart={canCreatorStart} joinedPlayers={joinedPlayerCount} minimumPlayers={minimumStartPlayers} />
                    ) : (
                        <div className={styles.diceDock} style={{
                            width: diceDockSize, height: diceDockSize,
                            borderRadius: 16,
                            background: !ended
                                ? (selfMoving
                                    ? 'linear-gradient(145deg, #1565c0 0%, #0d47a1 100%)'
                                    : 'linear-gradient(145deg, #1a237e 0%, #0d1550 100%)')
                                : 'rgba(255,255,255,0.06)',
                            border: selfMoving
                                ? '2.5px solid #42a5f5'
                                : movingPlayer
                                    ? `2.5px solid ${opponentDisplayPlayer?.color || 'rgba(255,255,255,0.2)'}`
                                    : '2px solid rgba(255,255,255,0.15)',
                            boxShadow: selfMoving
                                ? '0 0 24px rgba(66,165,245,0.7), 0 6px 16px rgba(0,0,0,0.6)'
                                : '0 4px 14px rgba(0,0,0,0.5)',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, transition: 'all 0.3s ease',
                            margin: compactGameViewport ? '0 4px' : '0 10px',
                        }}>
                            {!ended
                                ? (nowMoving
                                    ? <Dice playerColor={selfPlayer?.color} {...diceProps} alwaysVisible />
                                    : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', fontWeight: 800, letterSpacing: 0.4 }}>
                                            WAIT
                                        </div>
                                        <div style={{ fontSize: 9, color: getDisplayColor(movingPlayer) || 'rgba(255,255,255,0.45)', textAlign: 'center', lineHeight: 1.3, fontWeight: 600 }}>
                                            {opponentPlayer?.name || 'Opponent'}
                                            <br />
                                            turn
                                        </div>
                                    </div>)
                                : <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, textAlign: 'center', fontWeight: 700, letterSpacing: 1 }}>END</span>
                            }
                        </div>
                    )}

                    <div className={styles.hudPlayerColumn} data-player-role="opponent" data-display-color="green" data-visible-hex={TWO_PLAYER_VISIBLE_COLORS.opponent.hex} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: compactGameViewport ? 2 : 3, minWidth: 0, maxWidth: compactGameViewport ? 112 : 140 }}>
                        <PlayerAvatar player={opponentDisplayPlayer} isMoving={opponentMoving} flip={true} onSendEmoji={handleSendEmoji} compact={compactGameViewport} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 2, flexDirection: 'row-reverse' }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: opponentDisplayPlayer?.color || '#888', border: '1.5px solid white', flexShrink: 0 }} />
                            <div style={{ color: 'white', fontWeight: 700, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 80, textAlign: 'right' }}>
                                {opponentPlayer?.name || 'Opponent'}
                            </div>
                        </div>
                        {started && (
                            <div style={{ paddingRight: 2 }}>
                                <HeartsDisplay
                                    lives={livesMap[opponentPlayer?.color]}
                                    broken={heartBroken === opponentPlayer?.color}
                                    compact={compactGameViewport}
                                />
                            </div>
                        )}
                        {started && opponentMoving && <div style={{ color: '#facc15', fontSize: 10, paddingRight: 2 }}>Thinking… ▶</div>}
                    </div>
                </div>

                {/* Floating emoji reaction */}
                {floatingEmoji && (
                    <div style={{
                        position: 'fixed', bottom: 160, left: '50%', transform: 'translateX(-50%)',
                        fontSize: 56, zIndex: 200, pointerEvents: 'none',
                        animation: 'emojiFloat 2.5s ease-out forwards',
                    }}>{floatingEmoji}</div>
                )}
                {winnerOverlay}
                {chatPanel}
                {rtcOverlays}
                {ruleOverlay}
                <style>{`@keyframes emojiFloat{0%{opacity:1;transform:translateX(-50%) translateY(0) scale(1)}70%{opacity:0.9;transform:translateX(-50%) translateY(-60px) scale(1.3)}100%{opacity:0;transform:translateX(-50%) translateY(-120px) scale(0.8)}}@keyframes diceSpinWait{0%{transform:rotate(0deg) scale(1)}25%{transform:rotate(90deg) scale(1.15)}50%{transform:rotate(180deg) scale(1)}75%{transform:rotate(270deg) scale(1.15)}100%{transform:rotate(360deg) scale(1)}}@keyframes heartBreak{0%{transform:scale(1.5);filter:drop-shadow(0 0 4px #ff0000)}40%{transform:scale(0.8) rotate(-10deg)}70%{transform:scale(1.2) rotate(5deg)}100%{transform:scale(1);filter:none}}@keyframes urgentPulse{0%,100%{transform:scale(1);filter:drop-shadow(0 0 0 rgba(255,94,98,0))}50%{transform:scale(1.04);filter:drop-shadow(0 0 12px rgba(255,94,98,0.45))}}`}</style>
            </>
        );
    }

    // ══════════════════════════════════════════════════════════════════════════
    // 3/4-PLAYER: EXISTING LAYOUT
    // ══════════════════════════════════════════════════════════════════════════
    return (
        <>
            {viewportFrameStyle && <div style={viewportFrameStyle}><div style={viewportChromeStyle} /></div>}
            {topBar}
            <div
                className="w-full flex items-center justify-center px-2 pt-20 pb-4"
                style={{ paddingLeft: gameViewportInset + 8, paddingRight: gameViewportInset + 8 }}
            >
                <div
                    className='container'
                    data-testid="standard-player-layout"
                    data-room-mode={roomMode === 3 ? 'three-player' : 'four-player'}
                    data-active-player-count={Object.values(playersBySeat).filter(player => player && player.name !== '...').length}
                    style={{ transform: `scale(${boardScale})`, transformOrigin: 'top center' }}
                >
                    <Navbar
                        playersBySeat={playersBySeat}
                        viewLayout={viewLayout}
                        started={started}
                        time={time}
                        isReady={isReady}
                        movingPlayer={movingPlayer}
                        rolledNumber={rolledNumber}
                        nowMoving={nowMoving}
                        ended={ended}
                        moveSeqRef={moveSeqRef}
                        viewerIsCreator={viewerIsCreator}
                        canCreatorStart={canCreatorStart}
                        joinedPlayerCount={joinedPlayerCount}
                        minimumStartPlayers={minimumStartPlayers}
                    />
                    <Map pawns={visiblePawns} nowMoving={nowMoving} rolledNumber={rolledNumber} moveSeqRef={moveSeqRef} openPawnValues={openPawnValues} isTwoPlayerMode={isTwoPlayerMode} viewerColor={selfPlayer?.color || resolvedViewerColor} />
                </div>
            </div>
            {winnerOverlay}
            {chatPanel}
            {rtcOverlays}
            {ruleOverlay}
        </>
    );
};

export default Gameboard;
